import axios from "axios";
import Company from "../models/Company.js";
import VacancySmall from "../models/VacancySmall.js";
import Rater from "../models/Rater.js";
import { getAuth } from 'firebase/auth';
import Event from "../models/Event.js";
import Review from "../models/Review.js";

const API_URL = "https://europe-west3-hyper-e122e.cloudfunctions.net/get_company";
const SEARCH_API_URL = "https://europe-west3-hyper-e122e.cloudfunctions.net/search_companies"; 

export default class CompanyService {
  /**
   * 🔹 Получает данные о компании по ID
   * @param {string} companyId - ID компании
   * @returns {Promise<Company>} - Объект компании
   */
  static async fetchCompanyById(companyId) {
    try {
      const auth = getAuth();
  
      // Ждём, пока Firebase восстановит текущую сессию
      await auth.authStateReady();
  
      const user = auth.currentUser;
  
      if (!user) {
        console.warn(
          "❌ Пользователь не авторизован → редирект на стартовую"
        );
  
        window.location.replace(
          "https://hr3.space/"
        );
  
        return null;
      }
  
      const userId = user.uid;
  
      const response = await axios.get(
        API_URL,
        {
          params: {
            company_id: companyId,
            user_id: userId
          }
        }
      );
  
      if (response.data.error) {
        throw new Error(
          response.data.error
        );
      }
  
      console.log(
        "📡 Ответ сервера:",
        response.data
      );
  
      const companyData =
        response.data.company || {};
  
      const vacanciesData =
        response.data.vacancies || [];
  
      const ratersData =
        response.data.raters || [];
  
      const eventsData =
        response.data.events || [];
  
      const company =
        Company.fromJson(
          companyData
        );
  
      company.vacancies =
        vacanciesData.map(
          vacancy =>
            VacancySmall.fromJson(
              vacancy
            )
        );
  
      company.raters =
        ratersData.map(
          rater =>
            Rater.fromJson(
              rater
            )
        );
  
      company.events =
        eventsData.map(
          event =>
            Event.fromJson({
              ...event,
              city_id:
                event.city_id
                ?? company.regionId
            })
        );
  
      console.log(
        "👤 Current user:",
        userId
      );
  
      console.log(
        "⭐ is_followed:",
        company.isFollowed
      );
  
      return company;
    } catch (error) {
      console.error(
        "❌ Ошибка при загрузке компании:",
        error
      );
  
      throw error;
    }
  }

  /**
   * 🔹 Ищет компании по названию
   * @param {string} query - Поисковый запрос
   * @returns {Promise<Array>} - Список найденных компаний
   */
  static async searchCompanies(query) {
      const q = (query || "").trim();
      if (!q) return [];

      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) {
        console.warn("⚠️ Нет авторизованного пользователя — поиск недоступен");
        return [];
      }

      let token = await user.getIdToken(false);

      const doFetch = async (bearer) => {
        return axios.get(SEARCH_API_URL, {
          params: { query: q },
          headers: {
            "Authorization": `Bearer ${bearer}`,
            "Content-Type": "application/json",
          },
          timeout: 10000,
        });
      };

      try {
        const res = await doFetch(token);
        return res.data?.companies ?? [];
      } catch (err) {
        if (err?.response?.status === 401) {
          token = await user.getIdToken(true);
          const res = await doFetch(token);
          return res.data?.companies ?? [];
        }
        console.error("❌ Ошибка при поиске компаний:", err?.response?.data || err);
        return [];
      }
    }

    /**
 * 🔹 Ищет компании по названию (админский режим)
 * Принудительно обновляет кэш на сервере
 * @param {string} query
 * @returns {Promise<Array>}
 */
static async searchAdminCompanies(query) {
  const q = (query || "").trim();
  if (!q) return [];

  const auth = getAuth();
  const user = auth.currentUser;

  if (!user) {
    console.warn("⚠️ Нет авторизованного пользователя — поиск недоступен");
    return [];
  }

  let token = await user.getIdToken(false);

  const doFetch = async (bearer) => {
    return axios.get(SEARCH_API_URL, {
      params: {
        query: q,
        is_admin: "true",
        is_need_refresh_cache: "true",
      },
      headers: {
        "Authorization": `Bearer ${bearer}`,
        "Content-Type": "application/json",
      },
      timeout: 10000,
    });
  };

  try {
    const res = await doFetch(token);
    return res.data?.companies ?? [];
  } catch (err) {
    if (err?.response?.status === 401) {
      token = await user.getIdToken(true);
      const res = await doFetch(token);
      return res.data?.companies ?? [];
    }

    console.error("❌ Ошибка при админском поиске компаний:", err?.response?.data || err);
    return [];
  }
}

/**
 * 🔹 Загружает изображение компании (логотип или фон)
 * @param {File} file - Картинка для загрузки
 * @param {"logo"|"background"} type - Тип изображения
 * @returns {Promise<string>} - URL загруженного изображения
 */
static async uploadCompanyImage(file, type) {
  console.log("%c🚀 uploadCompanyImage START", "color:#4CAF50; font-weight:bold");

  if (!file || !["logo", "background"].includes(type)) {
    console.error("❌ Невалидные параметры:", { file, type });
    throw new Error("Невалидные параметры для загрузки изображения");
  }

  console.log("📌 Параметры:", { file, type });

  // --- Маппинг типов ---
  const TYPE_MAP = {
    logo:      { uploadType: "company_logo",  formField: "logo_image"  },
    background:{ uploadType: "company_cover", formField: "cover_image" },
  };

  const { uploadType, formField } = TYPE_MAP[type];

  console.log("🗂 TYPE_MAP:", { uploadType, formField });

  const auth = getAuth();
  const user = auth.currentUser;

  if (!user) {
    console.error("❌ Пользователь не авторизован");
    throw new Error("Пользователь не авторизован");
  }

  const token = await user.getIdToken(true);

  console.log("🔑 Firebase User:", {
    uid: user.uid,
    token: token.substring(0, 15) + "...(hidden)",
  });

  // --- Формируем FormData ---
  const formData = new FormData();
  formData.append("type", uploadType);
  formData.append("user_id", user.uid);
  formData.append(formField, file);

  console.log("📦 FormData содержимое:");
  console.log(" - type:", uploadType);
  console.log(" - user_id:", user.uid);
  console.log(" - file:", file);

  try {
    console.log("%c🌐 Отправка запроса на upload_image…", "color:#2196F3");

    const resp = await axios.post(
      "https://europe-west3-hyper-e122e.cloudfunctions.net/upload_image",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          "Authorization": `Bearer ${token}`,
        },
      }
    );

    console.log("📥 Ответ функции upload_image:", resp.data);

    if (resp.data?.error) {
      console.error("❌ Ошибка в ответе:", resp.data);
      throw new Error(resp.data.error);
    }

    console.log("%c✅ УСПЕХ uploadCompanyImage", "color:#4CAF50; font-weight:bold");
    console.log("🌍 Новый URL:", resp.data.url);

    return resp.data.url;

  } catch (error) {
    console.error("%c🔥 Ошибка uploadCompanyImage", "color:red; font-weight:bold");
    console.error(error);

    if (error.response) {
      console.error("📛 response.data:", error.response.data);
      console.error("📛 response.status:", error.response.status);
    }

    throw error;
  }
}

static async uploadCompanyAdminImage(file, type, companyId) {

  if (!file || !["logo", "background"].includes(type)) {
    throw new Error("Невалидные параметры для загрузки изображения");
  }

  const TYPE_MAP = {
    logo:      { uploadType: "company_logo",  formField: "logo_image" },
    background:{ uploadType: "company_cover", formField: "cover_image" },
  };

  const { uploadType, formField } = TYPE_MAP[type];

  const auth = getAuth();
  const user = auth.currentUser;

  if (!user) {
    throw new Error("Пользователь не авторизован");
  }

  const token = await user.getIdToken(true);

  const formData = new FormData();

  formData.append("type", uploadType);
  formData.append("user_id", user.uid);
  formData.append(formField, file);

  // 🔥 вот что нужно добавить
  formData.append("renew_url", "true");
  formData.append("company_id", companyId);

  const resp = await axios.post(
    "https://europe-west3-hyper-e122e.cloudfunctions.net/upload_image",
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
        "Authorization": `Bearer ${token}`,
      },
    }
  );

  return resp.data.url;
}

static async uploadCompanyExtraImagesWithRenew(file, type, renewUrl = false, companyId = null) {
  if (!file || !["logo", "background", "extra"].includes(type)) {
    throw new Error("Invalid upload type");
  }

  const TYPE_MAP = {
    logo:      { uploadType: "company_logo",  formField: "logo_image"  },
    background:{ uploadType: "company_cover", formField: "cover_image" },
    extra:     { uploadType: "company_image", formField: "company_image" },
  };

  const { uploadType, formField } = TYPE_MAP[type];

  const auth = getAuth();
  const user = auth.currentUser;
  const token = await user.getIdToken(true);

  const formData = new FormData();
  formData.append("type", uploadType);
  formData.append("user_id", user.uid);

  if (renewUrl && companyId) {
    formData.append("company_id", companyId);
    formData.append("renew_url", "true");
  }

  formData.append(formField, file);

  const resp = await axios.post(
    "https://europe-west3-hyper-e122e.cloudfunctions.net/upload_image",
    formData,
    {
      headers: {
        "Authorization": `Bearer ${token}`,
        "Content-Type": "multipart/form-data",
      }
    }
  );

  return resp.data.url;
}

static async uploadCompanyImageWithRenew(file, type, companyId) {
  console.log("%c🚀 uploadCompanyImage START", "color:#4CAF50; font-weight:bold");

  if (!file || !["logo", "background"].includes(type)) {
    console.error("❌ Невалидные параметры:", { file, type });
    throw new Error("Невалидные параметры для загрузки изображения");
  }

  const TYPE_MAP = {
    logo:      { uploadType: "company_logo",  formField: "logo_image"  },
    background:{ uploadType: "company_cover", formField: "cover_image" },
  };

  const { uploadType, formField } = TYPE_MAP[type];

  const auth = getAuth();
  const user = auth.currentUser;

  if (!user) throw new Error("Пользователь не авторизован");

  const token = await user.getIdToken(true);

  // --- FormData ---
  const formData = new FormData();
  formData.append("type", uploadType);
  formData.append("user_id", user.uid);
  formData.append(formField, file);

  // 🔥 ВАЖНО: обновляем URL (мы редактируем компанию)
  formData.append("renew_url", "true");
  formData.append("company_id", companyId);

  console.log("📦 FormData:", {
    uploadType,
    user: user.uid,
    renew_url: true,
    company_id: companyId
  });

  try {
    const resp = await axios.post(
      "https://europe-west3-hyper-e122e.cloudfunctions.net/upload_image",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          "Authorization": `Bearer ${token}`,
        },
      }
    );

    console.log("📥 upload_image →", resp.data);

    if (resp.data?.error) throw new Error(resp.data.error);

    return resp.data.url;

  } catch (err) {
    console.error("🔥 uploadCompanyImage ERROR:", err);
    throw err;
  }
}

     /**
     * 🔹 Получает список всех компаний (только name и company_id)
     * @returns {Promise<Array<{ company_id: string, name: string }>>}
     */
    static async fetchAllCompanies() {
      try {
        const auth = getAuth();
        const user = auth.currentUser;

        if (!user) {
          throw new Error("Пользователь не авторизован");
        }

        const token = await user.getIdToken();
        console.log("🔐 Токен получен (обрезан):", token.slice(0, 10) + "...");

        const response = await axios.get(
          "https://europe-west3-hyper-e122e.cloudfunctions.net/search_companies",
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.data.error) {
          throw new Error(response.data.error);
        }

        console.log("✅ Получены все компании:", response.data.companies);
        return response.data.companies || [];

      } catch (error) {
        console.error("❌ Ошибка при получении всех компаний:", error);
        return [];
      }
    }


/**
 * 🔹 Получает список всех компаний для админа
 * @returns {Promise<Array>}
 */
static async fetchAllAdminCompanies() {
  try {
    const auth =
      getAuth();

    /*
     * Ждём восстановления Firebase-сессии.
     */
    await auth.authStateReady();

    const user =
      auth.currentUser;

    if (!user) {
      console.warn(
        "❌ Пользователь не авторизован → редирект на стартовую"
      );

      window.location.replace(
        "https://hr3.space/"
      );

      return [];
    }

    const token =
      await user.getIdToken();

    const response =
      await axios.get(
        "https://europe-west3-hyper-e122e.cloudfunctions.net/search_companies",
        {
          params: {
            is_admin: true
          },

          headers: {
            Authorization:
              `Bearer ${token}`,
          },
        }
      );

    if (response.data.error) {
      throw new Error(
        response.data.error
      );
    }

    console.log(
      "👑 Получены админ-компании:",
      response.data.companies
    );

    return (
      response.data.companies ||
      []
    );

  } catch (error) {

    /*
     * Нет валидной сессии.
     */
    if (
      error?.response?.status === 401
    ) {
      console.warn(
        "❌ Firebase-сессия недействительна → редирект"
      );

      window.location.replace(
        "https://hr3.space/"
      );

      return [];
    }

    /*
     * Пользователь авторизован,
     * но backend не разрешает admin-доступ.
     */
    if (
      error?.response?.status === 403
    ) {
      console.warn(
        "⛔ Нет прав администратора → редирект"
      );

      window.location.replace(
        "https://hr3.space/"
      );

      return [];
    }

    console.error(
      "❌ Ошибка при получении админ-компаний:",
      error
    );

    return [];
  }
}


    /**
   * 🔄 Принудительно обновляет кэш компаний на сервере
   * @returns {Promise<Array>} - Список компаний после обновления
   */
  static async refreshCompaniesCacheAndSearch() {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      console.warn("⚠️ Нет авторизованного пользователя — обновление кэша недоступно");
      return [];
    }

    let token = await user.getIdToken(false);

    const doFetch = async (bearer) => {
      return axios.get(SEARCH_API_URL, {
        params: {
          is_need_refresh_cache: true
        },
        headers: {
          "Authorization": `Bearer ${bearer}`,
          "Content-Type": "application/json",
        },
        timeout: 10000,
      });
    };

    try {
      console.log("🔄 Запрос на обновление кэша компаний...");
      const res = await doFetch(token);
      return res.data?.companies ?? [];
    } catch (err) {
      // Если токен просрочен — обновим
      if (err?.response?.status === 401) {
        token = await user.getIdToken(true);
        const res = await doFetch(token);
        return res.data?.companies ?? [];
      }

      console.error("❌ Ошибка при обновлении кэша компаний:", err?.response?.data || err);
      return [];
    }
}

/**
 * 🔥 Создание компании
 * @param {Object} data - Объект из мастера (formData)
 * @returns {Promise<{ companyId: string }>}
 */
static async createCompany(data) {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error("Пользователь не авторизован");
    }

    const token = await user.getIdToken(true);

    // ✨ ПОЛНОСТЬЮ КОРРЕКТНЫЙ PAYLOAD (как iOS)
    const payload = {
      name: data.name,
      specifics: data.specifics,
      email: data.email,
      urlOfLogo: data.urlOfLogo,
      urlOfBackground: data.urlOfBackground,

      aboutCompany: data.aboutCompany,
      website: data.website,
      street: data.street,

      latitude: data.latitude,
      longitude: data.longitude,

      // 🔥 ПЛОСКИЕ соцсети (именно такие ждёт backend)
      facebook: data.socials?.facebook || "",
      instagram: data.socials?.instagram || "",
      linkedin: data.socials?.linkedin || "",
      twitter: data.socials?.twitter || "",
      telegram: data.socials?.telegram || "",
      youtube: data.socials?.youtube || ""
    };

    console.log("📤 Payload отправлен на backend:", payload);

    const response = await axios.post(
      "https://europe-west3-hyper-e122e.cloudfunctions.net/create_company",
      payload,
      {
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        }
      }
    );

    if (response.data?.error) {
      throw new Error(response.data.error);
    }

    console.log("🎉 Ответ от backend:", response.data);

    return response.data;

  } catch (err) {
    console.error("❌ Ошибка при создании компании:", err);
    throw err;
  }
}

static async getCompanyWithEditingOptions(companyId, allowEditing = true) {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) throw new Error("Пользователь не авторизован");

    const token = await user.getIdToken(true);

    const params = {
      company_id: companyId,
    };

    if (allowEditing) {
      params.allow_editing = "true";
      params.user_id = user.uid;
    }

    const response = await axios.get(
      "https://europe-west3-hyper-e122e.cloudfunctions.net/get_company",
      {
        headers: {
          "Authorization": `Bearer ${token}`,
        },
        params,
      }
    );

    const data = response.data;

    if (data.error) {
      throw new Error(data.error);
    }

    console.log("📦 Ответ get_company:", data);

    const companyData = data.company || {};
    const vacanciesData = data.vacancies || [];
    const ratersData = data.raters || [];
    const reviewsData = data.reviews || [];

    // --------------------------
    // ✅ Конвертация в модели
    // --------------------------
    const company = Company.fromJson(companyData);

    company.vacancies = vacanciesData.map(v => VacancySmall.fromJson(v));
    company.raters = ratersData.map(r => Rater.fromJson(r));
    company.reviews = reviewsData.map(r => Review.fromJson(r));

    // --------------------------
    // ⚡ Новые поля (важно!)
    // --------------------------
    company.isAllowEditing = companyData.is_allow_editing ?? false;
    company.isEditingRequestSent = companyData.is_editing_request_sent ?? false;

    console.log("🏢 Company loaded with editing flags:", {
      name: company.name,
      isAllowEditing: company.isAllowEditing,
      isEditingRequestSent: company.isEditingRequestSent
    });

    return company;

  } catch (err) {
    console.error("❌ Ошибка получения компании:", err);
    throw err;
  }
}

  static async requestCompanyEditAccess(companyId) {
    try {
      const auth = getAuth();
      const user = auth.currentUser;

      if (!user) throw new Error("Пользователь не авторизован");

      const token = await user.getIdToken(true);

      const url = "https://europe-west3-hyper-e122e.cloudfunctions.net/request_company_edit_access";

      const body = { company_id: companyId };

      const response = await axios.post(url, body, {
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });

      console.log("📬 Ответ сервера:", response.data);

      return response.data; // ← ВОТ ЭТО ПРАВИЛЬНО!

    } catch (error) {
      console.error("❌ Ошибка отправки запроса на редактирование:", error);

      if (error.response) {
        console.error("⚠ Ответ сервера:", error.response.data);
        throw new Error(error.response.data?.error || "Ошибка сервера");
      }

      throw error;
    }
  }

  static async updateCompanyRegion(companyId, regionID) {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) throw new Error("User not auth");
  
      const token = await user.getIdToken(true);
  
      const payload = {
        company_id: companyId,
        regionID: Number(regionID),  // 🔥 ОБЯЗАТЕЛЬНО
      };
  
      console.log("📦 payload updateCompanyRegion:", payload);
  
      await axios.post(
        "https://europe-west3-hyper-e122e.cloudfunctions.net/edit_company",
        payload,
        {
          headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
  
      return true;
  
    } catch (err) {
      console.error("❌ Ошибка обновления regionID:", err);
      throw err;
    }
  }

  static async updateAdminCompanyRegion(companyId, regionID) {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
  
      if (!user) throw new Error("User not auth");
  
      const token = await user.getIdToken(true);
  
      const payload = {
        company_id: companyId,
        regionID: Number(regionID),   // обязательно число
        is_admin: true                // 🔥 ключевая разница
      };
  
      console.log("🛠 CALL updateAdminCompanyRegion");
      console.log("🏢 companyId:", companyId);
      console.log("📦 payload:", payload);
  
      const response = await axios.post(
        "https://europe-west3-hyper-e122e.cloudfunctions.net/edit_company",
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );
  
      console.log("📨 Ответ updateAdminCompanyRegion:", response.data);
  
      return response.data;
  
    } catch (err) {
      console.error("🔥 Ошибка updateAdminCompanyRegion:", err);
      throw err;
    }
  }

  static async updateCompany(companyId, payload) {
    try {
      const auth = getAuth();
      const user = auth.currentUser;

      if (!user) {
        throw new Error("Пользователь не авторизован");
      }

      const token = await user.getIdToken(true);

      console.log("🌐 CALL updateCompany");
      console.log("🏢 companyId:", companyId);
      console.log("📦 payload:", payload);

      const response = await axios.post(
        "https://europe-west3-hyper-e122e.cloudfunctions.net/edit_company",
        {
          company_id: companyId,
          ...payload
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      console.log("📨 Ответ update_company:", response.data);

      return response.data;

    } catch (err) {
      console.error("🔥 Ошибка updateCompany:", err);
      throw err;
    }
  }

  static async updateAdminCompany(companyId, payload) {

    const auth = getAuth();
    const user = auth.currentUser;
  
    if (!user) {
      throw new Error("Пользователь не авторизован");
    }
  
    const token = await user.getIdToken(true);
  
    console.log("🛠 CALL updateAdminCompany");
    console.log("🏢 companyId:", companyId);
    console.log("📦 payload:", payload);
  
    const response = await axios.post(
      "https://europe-west3-hyper-e122e.cloudfunctions.net/edit_company",
      {
        company_id: companyId,
        is_admin: true,   
        ...payload
      },
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
  
    console.log("📨 Ответ updateAdminCompany:", response.data);
  
    return response.data;
  }

  // ============================================================
  // GET COMPANY WITH STRUCTURE
  // ============================================================

  /**
   * Получить компанию вместе со штатной структурой.
   *
   * Использует существующую Cloud Function get_company
   * с параметром structured=true.
   *
   * Сотрудники загружаются сервером из CompanyEmployees,
   * а информация о них — из Workers.
   *
   * Уровни:
   * 1 — Генеральный директор
   * 2 — Руководитель
   * 3 — Сотрудник
   *
   * Если level == null, сервер возвращает 3.
   *
   * @param {string} companyId - ID компании
   * @returns {Promise<Company>}
   */
  static async getCompanyWithStructure(companyId) {

    try {

      // ========================================================
      // VALIDATION
      // ========================================================

      if (
        typeof companyId !== "string" ||
        !companyId.trim()
      ) {
        throw new Error(
          "Не передан companyId"
        );
      }

      // ========================================================
      // AUTH
      // ========================================================

      const auth = getAuth();

      await auth.authStateReady();

      const user = auth.currentUser;

      if (!user) {
        throw new Error(
          "Пользователь не авторизован"
        );
      }

      // ========================================================
      // REQUEST
      // ========================================================

      const sendRequest = async (forceRefresh = false) => {

        const token = await user.getIdToken(forceRefresh);

        return axios.get(
          API_URL,
          {
            params: {
              company_id: companyId.trim(),
              structured: "true"
            },

            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json"
            },

            timeout: 30000
          }
        );

      };

      let response;

      try {

        response = await sendRequest(false);

      } catch (error) {

        // При истечении токена обновляем его и повторяем запрос.
        if (error?.response?.status === 401) {

          console.warn(
            "⚠️ Обновляем Firebase-токен и повторяем запрос"
          );

          response = await sendRequest(true);

        } else {

          throw error;

        }
      }

      // ========================================================
      // VALIDATE RESPONSE
      // ========================================================

      const data = response.data;

      if (!data || typeof data !== "object") {
        throw new Error(
          "Сервер вернул некорректный ответ"
        );
      }

      if (data.error) {
        throw new Error(data.error);
      }

      if (!data.company || typeof data.company !== "object") {
        throw new Error(
          "Данные компании отсутствуют в ответе"
        );
      }

      if (!Array.isArray(data.employees)) {
        throw new Error(
          "Сервер не вернул штатную структуру"
        );
      }

      // ========================================================
      // COMPANY MODEL
      // ========================================================

      const company = Company.fromJson(
        data.company
      );

      // ========================================================
      // VACANCIES
      // ========================================================

      company.vacancies = (
        Array.isArray(data.vacancies)
          ? data.vacancies
          : []
      ).map(
        vacancy => VacancySmall.fromJson(vacancy)
      );

      // ========================================================
      // EVENTS
      // ========================================================

      company.events = (
        Array.isArray(data.events)
          ? data.events
          : []
      ).map(
        event => Event.fromJson({
          ...event,
          city_id: event.city_id ?? company.regionId
        })
      );

      // ========================================================
      // REVIEWS
      // ========================================================

      company.reviews = (
        Array.isArray(data.reviews)
          ? data.reviews
          : []
      ).map(
        review => Review.fromJson(review)
      );

      // ========================================================
      // RATERS
      // ========================================================

      // В structured-режиме backend возвращает пустой
      // список raters, поскольку используем CompanyEmployees.

      company.raters = (
        Array.isArray(data.raters)
          ? data.raters
          : []
      ).map(
        rater => Rater.fromJson(rater)
      );

      // ========================================================
      // STRUCTURED EMPLOYEES
      // ========================================================

      company.employees = data.employees.map(employee => {

        // level == null на всякий случай приводим к 3.
        // Backend уже делает такую нормализацию.
        const rawLevel = employee.level;

        const level = (
          rawLevel === 1 ||
          rawLevel === 2 ||
          rawLevel === 3
        )
          ? rawLevel
          : 3;

        // Статус сохраняем без догадок:
        // true  — работает
        // false — больше не работает
        // null  — неизвестно
        const isCurrentEmployee =
          typeof employee.isCurrentEmployee === "boolean"
            ? employee.isCurrentEmployee
            : null;

        return {
          relationId: employee.relationId,
          companyId: employee.companyId,
          userId: employee.userId,

          name: employee.name || "Пользователь",
          age: employee.age ?? null,
          specifics: employee.specifics || "Не указано",
          imageURL: employee.imageURL || "",

          level,
          isCurrentEmployee
        };

      });

      // ========================================================
      // SORT BY LEVEL
      // ========================================================

      company.employees.sort((a, b) => {

        if (a.level !== b.level) {
          return a.level - b.level;
        }

        return a.name.localeCompare(
          b.name,
          "ru"
        );

      });

      // ========================================================
      // EDITING FLAGS
      // ========================================================

      company.isAllowEditing =
        data.company.is_allow_editing ?? false;

      company.isEditingRequestSent =
        data.company.is_editing_request_sent ?? false;

      // ========================================================
      // SUCCESS
      // ========================================================

      console.log(
        "🏢 Компания со штатной структурой загружена:",
        {
          companyId: company.id,
          companyName: company.name,
          employeesCount: company.employees.length,
          employees: company.employees
        }
      );

      return company;

    } catch (error) {

      const errorMessage = axios.isAxiosError(error)
        ? (
            error.response?.data?.error ||
            error.message
          )
        : error?.message;

      console.error(
        "❌ Ошибка getCompanyWithStructure:",
        errorMessage
      );

      throw new Error(
        errorMessage ||
        "Не удалось загрузить штатную структуру"
      );

    }
  }


}